import React, { useState, useEffect } from "react";
import { Box, Tabs, Tab, Paper, Container } from "@mui/material";
import {
  CircularProgress,
  Button,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Skeleton } from "@mui/material";
import TashIcon from "@mui/icons-material/Delete";
import PromoteIcon from "@mui/icons-material/SwitchAccessShortcut";
import DemoteIcon from "@mui/icons-material/RemoveModerator";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MUIConfirm from "../../../components/UI/MUIConfirm";
import MUIAlert from "../../../components/UI/MUIAlert";
import Divider from "@mui/material/Divider";
import { getUsers, deleteUserById } from "../../../api/users";
import { Helmet } from "react-helmet-async";

const UserManagement = () => {
  const theme = useTheme();
  const [error, setError] = useState(null);
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);

  const [loading, setLoading] = useState(true); // Add loading state

  const [submittingDelete, setSubmittingDelete] = useState(false); // Add loading state
  const [submittingPromote, setSubmittingPromote] = useState(false); // Add loading state
  const [submittingDemote, setSubmittingDemote] = useState(false); // Add loading state

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState("");
  const [confirmContent, setConfirmContent] = useState("");
  const [confirmCallback, setConfirmCallback] = useState(null);
  const [confirmParameter, setconfirmParameter] = useState(null);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success"); // Default severity

  const showAlert = (message, severity = "success") => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  const handleAlertClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertOpen(false);
  };

  const showConfirm = (title, content, onConfirm, parameter) => {
    setConfirmTitle(title);
    setConfirmContent(content);
    setConfirmCallback(() => onConfirm);
    setconfirmParameter(parameter);
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    setConfirmOpen(false);
    setConfirmTitle("");
    setConfirmContent("");
    setConfirmCallback(null);
    setconfirmParameter(null);
  };

  const handleConfirmAction = () => {
    if (confirmCallback && confirmParameter) {
      confirmCallback(confirmParameter);
    }
    handleConfirmClose();
  };

  const handleDeleteUser = async (userId) => {
    showConfirm(
      t("admin.delete_user_confirmation_title"),
      t("admin.delete_user_confirmation"),
      async (userId) => {
        setSubmittingDelete(true);
        try {
          await deleteUserById(userId);
          fetchUsers(setUsers, setLoading, setError);
          showAlert(t("admin.delete_user_success"));
          setSubmittingDelete(false);
        } catch (error) {
          showAlert(t("admin.delete_user_failure"), "error");
          console.error("Failed to delete user:", error);
          setError("Failed to delete user.");
          setSubmittingDelete(false);
        }
      },
      userId // Pass the auctionId to the showConfirm function
    );
  };

  const handlePromoteUser = async (userId) => {
    // showConfirm(
    //     t("my_auctions.extend_confirmation_title"),
    //     t("my_auctions.extend_confirmation"),
    //     async (auctionId) => {
    //         setSubmittingExtend(true); // Set loading to true while extending
    //         try {
    //             await extendAuction(auctionId);
    //             fetchAuctions(setAuctions, setLoading, setError);
    //             showAlert(t("my_auctions.extend_success"));
    //             setSubmittingExtend(false); // Set loading to true while extending
    //         } catch (error) {
    //             showAlert(t("my_auctions.extend_failure"), 'error');
    //             console.error("Failed to extend auction:", error);
    //             setError('Failed to extend auction.');
    //             setSubmittingExtend(false); // Set loading to true while extending
    //         }
    //     }, auctionId)
  };

  const handleDemoteUser = async (userId) => {
    // showConfirm(
    //     t("my_auctions.extend_confirmation_title"),
    //     t("my_auctions.extend_confirmation"),
    //     async (auctionId) => {
    //         setSubmittingExtend(true); // Set loading to true while extending
    //         try {
    //             await extendAuction(auctionId);
    //             fetchAuctions(setAuctions, setLoading, setError);
    //             showAlert(t("my_auctions.extend_success"));
    //             setSubmittingExtend(false); // Set loading to true while extending
    //         } catch (error) {
    //             showAlert(t("my_auctions.extend_failure"), 'error');
    //             console.error("Failed to extend auction:", error);
    //             setError('Failed to extend auction.');
    //             setSubmittingExtend(false); // Set loading to true while extending
    //         }
    //     }, auctionId)
  };

  const fetchUsers = async (setUsers, setLoading, setError) => {
    try {
      const fetchedUsers = await getUsers();
      setUsers(fetchedUsers);
      setLoading(false); // Set loading to false after data is fetched
    } catch (error) {
      console.error("Failed to fetch users:", error);
      setError("Failed to fetch users.");
      setLoading(false); // Set loading to false on error as well
    }
  };

  useEffect(() => {
    fetchUsers(setUsers, setLoading, setError);
  }, []);

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Container maxWidth="lg">
        <Container sx={{ mt: 4, mb: 4 }}>
          <Box>
            <Box sx={{ position: "relative" }}>
              <Typography variant="h4" gutterBottom>
                {t("admin.users")}
              </Typography>

              {loading ? ( // Show skeleton
                <Paper sx={{ padding: 2, marginBottom: 4 }}>
                  <Typography variant="h6" gutterBottom>
                    <Skeleton width="40%" />
                  </Typography>
                  <List>
                    {Array.from({ length: 2 }).map((_, index) => (
                      <ListItem key={index} sx={{ marginBottom: 1 }}>
                        <ListItemText
                          primary={<Skeleton width="60%" />}
                          secondary={<Skeleton width="40%" />}
                        />
                        <ListItemText
                          primary={<Skeleton width="30%" />}
                          secondary={<Skeleton width="30%" />}
                        />
                        <ListItemSecondaryAction>
                          <Skeleton
                            width="100px"
                            height="40px"
                            style={{ marginRight: 1 }}
                          />
                          <Skeleton
                            width="80px"
                            height="40px"
                            style={{ marginRight: 1 }}
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              ) : users.length > 0 ? (
                <Paper sx={{ padding: 2, marginBottom: 4 }}>
                  <List>
                    {users.map((user, index) => {
                      const isLast = index === users.length - 1;
                      return (
                        <>
                          <ListItem
                            key={user.user_id}
                            sx={{
                              marginBottom: 1,
                            }}
                          >
                            <ListItem
                              button
                              key={user.user_id}
                              sx={{ marginBottom: 1 }}
                            >
                              <ListItemText
                                primary={
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    <Typography
                                      variant="body1"
                                      fontWeight={500}
                                    >
                                      [{user.id}] {user.first_name}{" "}
                                      {user.last_name}
                                    </Typography>
                                    {user.role == "admin" ? (
                                      <Paper
                                        component="span"
                                        sx={{
                                          backgroundColor:
                                            theme.palette.success.light,
                                          color:
                                            theme.palette.success.contrastText,
                                          px: 1,
                                          fontSize: "0.75rem",
                                          fontWeight: 500,
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {user.role}
                                      </Paper>
                                    ) : (
                                      <Paper
                                        component="span"
                                        sx={{
                                          backgroundColor:
                                            theme.palette.grey[300],
                                          color: theme.palette.text.primary,
                                          px: 1,
                                          fontSize: "0.75rem",
                                          fontWeight: 500,
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {user.role}
                                      </Paper>
                                    )}
                                  </Box>
                                }
                                secondary={
                                  <Box
                                    sx={{
                                      mt: 0.5,
                                      display: "flex",
                                      gap: 2,
                                      flexWrap: "wrap",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      color="text.primary"
                                    >
                                      {user.email}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {user.phone}
                                    </Typography>
                                  </Box>
                                }
                                // TODO show invoice number and status

                                sx={{ color: theme.palette.text.primary }}
                              />
                            </ListItem>

                            <ListItemSecondaryAction>
                              {/* {user.role == "admin" ?
                                                        <Button
                                                            variant="outlined"
                                                            color="primary"
                                                            onClick={() => handleDemoteUser(user.id)}
                                                            endIcon={<DemoteIcon />}
                                                            sx={{ marginRight: 1 }}
                                                        >
                                                            {submittingDemote ? (
                                                                <CircularProgress size={24} color="inherit" /> // Show spinner when loading
                                                            ) : (
                                                                t("admin.demote")
                                                            )}
                                                        </Button>
                                                        : <Button
                                                            variant="outlined"
                                                            color="primary"
                                                            onClick={() => handlePromoteUser(user.id)}
                                                            endIcon={<PromoteIcon />}
                                                            sx={{ marginRight: 1 }}
                                                        >
                                                            {submittingPromote ? (
                                                                <CircularProgress size={24} color="inherit" /> // Show spinner when loading
                                                            ) : (
                                                                t("admin.promote")
                                                            )}
                                                        </Button>} */}

                              {/* TODO add BAN user and UNABAN user */}
                              {/* ADD Verify User */}

                              {/* DELETE ITEM */}
                              <Button
                                variant="outlined"
                                color="error"
                                endIcon={<TashIcon />}
                                onClick={() => handleDeleteUser(user.id)}
                              >
                                {submittingDelete ? (
                                  <CircularProgress size={24} color="inherit" />
                                ) : (
                                  t("my_auctions.delete")
                                )}
                              </Button>
                            </ListItemSecondaryAction>
                          </ListItem>
                          {!isLast ? (
                            <Divider orientation="horizontal" flexItem />
                          ) : null}
                        </>
                      );
                    })}
                  </List>
                </Paper>
              ) : (
                <></>
              )}
            </Box>
          </Box>
        </Container>

        <MUIConfirm
          open={confirmOpen}
          onClose={handleConfirmClose}
          onConfirm={handleConfirmAction}
          title={confirmTitle}
          content={confirmContent}
        />
        <MUIAlert
          open={alertOpen}
          onClose={handleAlertClose}
          severity={alertSeverity}
          message={alertMessage}
        />
      </Container>
    </>
  );
};

export default UserManagement;
